import React, { useMemo } from 'react';
import { Button, Dropdown, Tooltip, message as antMessage } from 'antd';
import {
  CopyOutlined,
  ShareAltOutlined,
  MailOutlined,
  WhatsAppOutlined,
  FacebookOutlined,
  LinkedinOutlined,
  XOutlined,
} from '@ant-design/icons';
import {
  buildShareLinks,
  copyTextToClipboard,
  formatMessageShareText,
  openShareUrl,
} from '../../utils/messageCopyShare';

/**
 * Reusable copy + share actions for plain text (chat messages, snippets, etc.).
 *
 * @param {object} props
 * @param {string} [props.text] - Raw text to copy/share (if `message` omitted)
 * @param {object} [props.message] - Team assistant message; formatted via formatMessageShareText
 * @param {string} [props.emailSubject]
 * @param {string} [props.pageUrl]
 * @param {boolean} [props.disabled]
 * @param {'small'|'middle'} [props.size]
 * @param {string} [props.className]
 */
const MessageCopyShareActions = ({
  text,
  message,
  emailSubject = 'Team assistant message',
  pageUrl,
  disabled = false,
  size = 'small',
  className = '',
}) => {
  const shareText = useMemo(() => {
    if (message) return formatMessageShareText(message, { pageUrl });
    return (text || '').trim();
  }, [message, text, pageUrl]);

  const isDisabled = disabled || !shareText;

  const links = useMemo(
    () => buildShareLinks(shareText, { subject: emailSubject, pageUrl }),
    [shareText, emailSubject, pageUrl]
  );

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(shareText);
    if (ok) {
      antMessage.success('Copied to clipboard');
    } else {
      antMessage.error('Could not copy');
    }
  };

  const shareItems = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: <WhatsAppOutlined />,
      onClick: () => openShareUrl(links.whatsapp),
    },
    {
      key: 'email',
      label: 'Email',
      icon: <MailOutlined />,
      onClick: () => {
        window.location.href = links.email;
      },
    },
    {
      key: 'twitter',
      label: 'X (Twitter)',
      icon: <XOutlined />,
      onClick: () => openShareUrl(links.twitter),
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: <FacebookOutlined />,
      onClick: () => openShareUrl(links.facebook),
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: <LinkedinOutlined />,
      onClick: () => openShareUrl(links.linkedin),
    },
  ];

  return (
    <div
      className={`message-copy-share-actions ${className}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip title="Copy">
        <Button
          type="text"
          size={size}
          className="message-copy-share-actions-btn"
          icon={<CopyOutlined />}
          disabled={isDisabled}
          aria-label="Copy message"
          onClick={handleCopy}
        />
      </Tooltip>
      <Dropdown
        menu={{ items: shareItems }}
        trigger={['click']}
        placement="bottomRight"
        disabled={isDisabled}
      >
        <Tooltip title="Share">
          <Button
            type="text"
            size={size}
            className="message-copy-share-actions-btn"
            icon={<ShareAltOutlined />}
            disabled={isDisabled}
            aria-label="Share message"
          />
        </Tooltip>
      </Dropdown>
    </div>
  );
};

export default MessageCopyShareActions;
